import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';

/**
 * Landing screen for a room-invite deep link (`/online/join/:code`) —
 * reached either via DeepLinkListener.jsx (a real `rondaybaciga://
 * join-room?code=...` tap) or by typing the URL directly. Wrapped in
 * RequireOnlineAuth like every other online route, so an unauthenticated
 * tap goes through login first and comes back here automatically
 * (RequireOnlineAuth's existing `state:{from}` pattern).
 */
export default function JoinByLinkScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { code } = useParams();
    const { authFetch } = useAuth();

    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const room = await authFetch(`/rooms/${encodeURIComponent(code)}/join`, { method: 'POST' });
                if (!cancelled) navigate(`/online/room/${room.id}`, { replace: true });
            } catch (err) {
                if (!cancelled) setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined)));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authFetch, code, navigate, t]);

    return (
        <>
            <Header title={t('online.join.title')} onBack={() => navigate('/online')} showSettings={false} />
            <Container maxWidth="xs" sx={{ mt: 6, textAlign: 'center' }}>
                {error ? (
                    <Box>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                        <Button variant="contained" onClick={() => navigate('/online')}>
                            {t('online.lobby.title')}
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography>{t('online.join.joining', { code })}</Typography>
                    </Box>
                )}
            </Container>
        </>
    );
}
